import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { toast } from 'sonner';
import axios from 'axios';

import { Settings, Calendar, Users, Building, Briefcase, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const _RCDS_TYPES = [
  { id: 'annuel', label: 'RCDS Annuel', icon: Calendar, color: 'emerald' },
  { id: 'nominatif', label: 'RCDS Nominatif', icon: Briefcase, color: 'blue' }
];

const _ESPACES = [
  { id: 'super_admin', label: 'Super Admin', icon: Shield },
  { id: 'admin', label: 'Admin', icon: Settings },
  { id: 'cooper', label: "Cooper's", icon: Building },
  { id: 'member', label: 'Membre', icon: Users },
  { id: 'expert', label: 'Expert', icon: Briefcase }
];

export function useBaticoConfig(getAuthHeader) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingItem, setUpdatingItem] = useState(null); // Track which item is being updated
  const [activeTab, setActiveTab] = useState('seuils');
  const [activeRcds, setActiveRcds] = useState('annuel');
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [archives, setArchives] = useState({ taxes: [], remises: [], activites: [] });
  const [stats, setStats] = useState({});
  const [selectedEspaces, setSelectedEspaces] = useState([]);
  
  // Local state for taxes and remises to enable optimistic updates
  const [taxes, setTaxes] = useState([]);
  const [remises, setRemises] = useState([]);
  const [coefficients, setCoefficients] = useState({});
  
  // Debounce ref
  const updateTimeoutRef = useRef(null);
  
  // Forms
  const [seuilForm, setSeuilForm] = useState({ annuel: {}, nominatif: {} });
  const [eligForm, setEligForm] = useState({ annuel: {}, nominatif: {} });
  const [newTaxe, setNewTaxe] = useState({ nom: '', taux: '', description: '' });
  const [newRemise, setNewRemise] = useState({ nom: '', pourcentage: '', conditions: '' });
  const [editingId, setEditingId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();
      
      const [configRes, historyRes, archivesRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/batico/config/v3`, { headers }),
        axios.get(`${API}/api/batico/config/v3/history?limit=30`, { headers }),
        axios.get(`${API}/api/batico/config/v3/archives`, { headers }),
        axios.get(`${API}/api/batico/config/v3/statistics`, { headers })
      ]);
      
      if (configRes.data.success) {
        const configData = configRes.data.config;
        setConfig(configData);
        setSeuilForm({
          annuel: configData.seuils_ca?.annuel || { seuil_minimum: 5000, actif: true },
          nominatif: configData.seuils_ca?.nominatif || { seuil_minimum: 5000, actif: true }
        });
        setEligForm({
          annuel: configData.seuils_ca_eligibilite?.annuel || { seuil_ca: 40000, actif: false, message: '' },
          nominatif: configData.seuils_ca_eligibilite?.nominatif || { seuil_ca: 5000, actif: false, message: '' }
        });
        // Initialize local state for taxes, remises, coefficients
        setTaxes(configData.taxes?.[activeRcds] || []);
        setRemises(configData.remises?.[activeRcds] || []);
        setCoefficients(configData.coefficients?.[activeRcds] || {});
      }
      if (historyRes.data.success) setHistory(historyRes.data.history);
      if (archivesRes.data.success) setArchives(archivesRes.data.archives || { taxes: [], remises: [], activites: [] });
      if (statsRes.data.success) setStats(statsRes.data.statistics || {});
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, activeRcds]);

  // Update local state when activeRcds changes
  useEffect(() => {
    if (config) {
      setTaxes(config.taxes?.[activeRcds] || []);
      setRemises(config.remises?.[activeRcds] || []);
      setCoefficients(config.coefficients?.[activeRcds] || {});
    }
  }, [activeRcds, config]);

  useEffect(() => { loadData(); }, [loadData]);

  // ==================== HANDLERS ====================

  const handleSaveSeuil = async (type) => {
    try {
      setSaving(true);
      await axios.put(`${API}/api/batico/config/v3/seuils-ca/${type}`, {
        seuil_minimum: parseFloat(seuilForm[type].seuil_minimum) || 5000,
        message_seuil: seuilForm[type].message_seuil,
        actif: seuilForm[type].actif !== false
      }, { headers: getAuthHeader() });
      toast.success('Seuil sauvegardé');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSeuil = async (type) => {
    try {
      await axios.post(`${API}/api/batico/config/v3/seuils-ca/${type}/toggle`, {}, { headers: getAuthHeader() });
      toast.success('Seuil basculé');
      loadData();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleSaveEligibilite = async (type) => {
    try {
      setSaving(true);
      await axios.put(`${API}/api/batico/config/v3/seuils-ca-eligibilite/${type}`, {
        seuil_ca: parseFloat(eligForm[type].seuil_ca) || 0,
        message: eligForm[type].message,
        actif: eligForm[type].actif === true,
      }, { headers: getAuthHeader() });
      toast.success("Seuil d'éligibilité sauvegardé");
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEligibilite = async (type) => {
    try {
      await axios.post(`${API}/api/batico/config/v3/seuils-ca-eligibilite/${type}/toggle`, {}, { headers: getAuthHeader() });
      toast.success("Seuil d'éligibilité basculé");
      loadData();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleAddTaxe = async () => {
    if (!newTaxe.nom || !newTaxe.taux) { toast.error('Remplissez tous les champs'); return; }
    try {
      await axios.post(`${API}/api/batico/config/v3/taxes/${activeRcds}`, {
        nom: newTaxe.nom,
        taux: parseFloat(newTaxe.taux),
        description: newTaxe.description
      }, { headers: getAuthHeader() });
      toast.success('Taxe ajoutée');
      setNewTaxe({ nom: '', taux: '', description: '' });
      loadData();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleUpdateTaxe = async (taxeId, data) => {
    // Prevent multiple simultaneous updates
    if (updatingItem === taxeId) return;
    
    try {
      setUpdatingItem(taxeId);
      
      // Optimistic update - update local state immediately
      setTaxes(prevTaxes => prevTaxes.map(t => t.id === taxeId ? { ...t, ...data } : t));
      
      // Debounce the API call
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      updateTimeoutRef.current = setTimeout(async () => {
        try {
          await axios.put(`${API}/api/batico/config/v3/taxes/${activeRcds}/${taxeId}`, data, { headers: getAuthHeader() });
          toast.success('Taxe mise à jour');
          setEditingId(null);
        } catch {
          // Revert on error
          loadData();
          toast.error('Erreur de mise à jour');
        } finally {
          setUpdatingItem(null);
        }
      }, 300);
      
    } catch {
      setUpdatingItem(null);
      toast.error('Erreur');
    }
  };

  const handleArchiveTaxe = async (taxeId) => {
    try {
      // Optimistic update
      setTaxes(prevTaxes => prevTaxes.filter(t => t.id !== taxeId));
      
      await axios.delete(`${API}/api/batico/config/v3/taxes/${activeRcds}/${taxeId}`, { headers: getAuthHeader() });
      toast.success('Taxe archivée');
      // Reload to sync archives
      loadData();
    } catch {
      loadData(); // Revert
      toast.error('Erreur');
    }
  };

  const handleAddRemise = async () => {
    if (!newRemise.nom || !newRemise.pourcentage) { toast.error('Remplissez les champs'); return; }
    try {
      await axios.post(`${API}/api/batico/config/v3/remises/${activeRcds}`, {
        nom: newRemise.nom,
        pourcentage: parseFloat(newRemise.pourcentage),
        conditions: newRemise.conditions
      }, { headers: getAuthHeader() });
      toast.success('Remise ajoutée');
      setNewRemise({ nom: '', pourcentage: '', conditions: '' });
      loadData();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleUpdateCoefficients = async (coefs) => {
    // Optimistic update
    setCoefficients(coefs);
    
    try {
      await axios.put(`${API}/api/batico/config/v3/coefficients/${activeRcds}`, coefs, { headers: getAuthHeader() });
      toast.success('Coefficients mis à jour');
    } catch {
      loadData(); // Revert on error
      toast.error('Erreur');
    }
  };

  const handleSync = async (configType) => {
    if (selectedEspaces.length === 0) { toast.error('Sélectionnez des espaces'); return; }
    try {
      setSaving(true);
      await axios.post(`${API}/api/batico/config/v3/sync`, {
        config_type: configType,
        type_rcds: activeRcds,
        espaces: selectedEspaces
      }, { headers: getAuthHeader() });
      toast.success(`Synchronisé vers ${selectedEspaces.length} espace(s)`);
      setSelectedEspaces([]);
      loadData();
    } catch {
      toast.error('Erreur de synchronisation');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreArchive = async (type, itemId) => {
    try {
      await axios.post(`${API}/api/batico/config/v3/archives/restore`, {
        type,
        item_id: itemId,
        type_rcds: activeRcds
      }, { headers: getAuthHeader() });
      toast.success('Élément restauré avec succès');
      loadData();
    } catch {
      toast.error('Erreur lors de la restauration');
    }
  };

  const handleDeletePermanently = async (type, itemId) => {
    if (!window.confirm('Supprimer définitivement cet élément ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`${API}/api/batico/config/v3/archives/${type}/${itemId}`, { headers: getAuthHeader() });
      toast.success('Élément supprimé définitivement');
      loadData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('fr-FR') : 'Jamais';

  // Use state variables for taxes, remises, coefficients - already declared above
  // Filter out archived taxes for display - MUST be before any early returns (Rules of Hooks)
  const displayTaxes = useMemo(() => taxes.filter(t => !t.archived), [taxes]);
  const displayRemises = useMemo(() => remises.filter(r => !r.archived), [remises]);
  const nomenclatures = useMemo(() => config?.nomenclatures || [], [config]);
  const categories = useMemo(() => config?.categories || [], [config]);
  const activites = useMemo(() => config?.activites || [], [config]);
  return { activeRcds, activeTab, activites, archives, categories, coefficients, config, displayRemises, displayTaxes, editingId, eligForm, expandedSections, formatDate, getAuthHeader, handleAddRemise, handleAddTaxe, handleArchiveTaxe, handleDeletePermanently, handleRestoreArchive, handleSaveEligibilite, handleSaveSeuil, handleSync, handleToggleEligibilite, handleToggleSeuil, handleUpdateCoefficients, handleUpdateTaxe, history, loadData, loading, newRemise, newTaxe, nomenclatures, remises, saving, selectedEspaces, setActiveRcds, setActiveTab, setArchives, setCoefficients, setConfig, setEditingId, setEligForm, setExpandedSections, setHistory, setLoading, setNewRemise, setNewTaxe, setRemises, setSaving, setSelectedEspaces, setSeuilForm, setStats, setTaxes, setUpdatingItem, seuilForm, stats, taxes, updateTimeoutRef, updatingItem };
}
